import { Box, Tooltip, ListItemButtonProps, Badge } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useLocales from '../../../hooks/useLocales';
import RoleBasedGuard from '../../../guards/RoleBasedGuard';
import Iconify from '../../Iconify';
import { NavItemProps } from '../type';
import { ListItemStyle, ListItemTextStyle, ListItemIconStyle } from './style';
import Label from '../../Label';
import { ICON } from '../../../config';

type Props = NavItemProps & ListItemButtonProps;

export default function NavItem({ item, depth, active, open, isCollapse, ...other }: Props) {
    const { translate } = useLocales();
    const theme = useTheme();

    const { title, icon, info, children, disabled, caption, roles, badgeContent } = item;

    const renderContent = (
        <ListItemStyle
            depth={depth}
            active={active}
            disabled={disabled}
            aria-current={active && !children ? 'page' : undefined}
            sx={{
                gap: 2,
                justifyContent: isCollapse ? 'center' : 'flex-start',
                pr: isCollapse ? 2 : 1.5,
            }}
            {...other}
        >
            {icon && (
                <ListItemIconStyle sx={{ mr: 0 }}>
                    <Badge
                        color="error"
                        variant="standard"
                        invisible={!isCollapse}
                        badgeContent={badgeContent}
                        sx={{
                            width: ICON.NAVBAR_ITEM,
                            height: ICON.NAVBAR_ITEM,
                        }}
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                        {icon}
                    </Badge>
                </ListItemIconStyle>
            )}

            {depth !== 1 && <DotIcon active={active && depth !== 1} />}

            {!isCollapse && (
                <ListItemTextStyle
                    primary={translate(title)}
                    secondary={
                        caption && (
                            <Tooltip title={translate(caption)} placement="top-start">
                                <span>{translate(caption)}</span>
                            </Tooltip>
                        )
                    }
                    primaryTypographyProps={{
                        noWrap: true,
                        variant: 'body2',
                        fontWeight: 500,
                        color: active ? theme.palette.primary.main : 'text.secondary',
                    }}
                    secondaryTypographyProps={{
                        noWrap: true,
                        variant: 'caption',
                    }}
                />
            )}

            {!isCollapse && badgeContent ? (
                <Label color="error" variant="filled">
                    {badgeContent}
                </Label>
            ) : null}

            {!isCollapse && (
                <>
                    {info && (
                        <Box component="span" sx={{ lineHeight: 0 }}>
                            {info}
                        </Box>
                    )}

                    {!!children && (
                        <Iconify
                            icon={
                                open ? 'eva:arrow-ios-downward-fill' : 'eva:arrow-ios-forward-fill'
                            }
                            sx={{ width: 16, height: 16, ml: 1, flexShrink: 0 }}
                        />
                    )}
                </>
            )}
        </ListItemStyle>
    );

    return <RoleBasedGuard roles={roles}>{renderContent}</RoleBasedGuard>;
}

type DotIconProps = {
    active: boolean;
};

export function DotIcon({ active }: DotIconProps) {
    return (
        <ListItemIconStyle>
            <Box
                component="span"
                sx={{
                    width: 4,
                    height: 4,
                    borderRadius: '50%',
                    bgcolor: 'text.disabled',
                    transition: (theme) =>
                        theme.transitions.create('transform', {
                            duration: theme.transitions.duration.shorter,
                        }),
                    ...(active && {
                        transform: 'scale(2)',
                        bgcolor: 'primary.main',
                    }),
                }}
            />
        </ListItemIconStyle>
    );
}
